import { exactCurrencyFormatter, unreachable, useFetch, Uuid } from "@/utils"
import React from "preact/compat"
import { api_fetch_throws, CheckStatus, DeductionExt, DeductionListJson, DeductionDetailJson, RepaymentSummary, TransactionJson } from "@/api"
import { RefreshFlag } from "@/refresh_flag.tsx"
import { CenterSpinner } from "@/app/loading.tsx"
import { DetailNavbar } from "@/app/navbar.tsx"
import { format, parseISO } from "date-fns"
import { copyOnAltClick } from "@/deduction_table/util.tsx"
import { capitalCase } from "change-case"
import { useLocation } from "wouter-preact"
import { ExternalLinkIcon } from "lucide-react"
import { closeModalAndNavigate } from "@/deduction/deduction_detail_modal_state"
import { closeAccountingTransactionModal } from "./accounting_transaction_modal_state"
import { AccountingTransactionContent } from "./AccountingTransactionContent.tsx"
import { EditableTransactionAmount } from "./EditableTransactionAmount.tsx"

interface DeductionHeaderProps {
  deduction: DeductionExt
  children?: React.ReactNode
}

export function AccountingStatus({ status }: { status: CheckStatus | undefined }) {
  let color
  let display
  if (status === "new" || status === undefined) {
    color = "bg-yellow-500"
    display = "Uncoded"
  } else if (status === "split") {
    color = "bg-orange-500"
    display = "Split"
  } else if (status === "coded") {
    color = "bg-green-500"
    display = "Coded"
  } else {
    unreachable(status)
  }
  return <span class={`px-2 py-1 font-medium rounded-sm ${color}`}>{display}</span>
}

export function DeductionHeaderLeft({ deduction, children }: DeductionHeaderProps) {
  const [, setLocation] = useLocation()
  
  let kind
  if (deduction.kind === "repayment") {
    kind = "Repayment"
  } else if ((deduction as any).kind === "check") {
    kind = "Check"
  } else {
    kind = capitalCase(deduction.kind)
  }

  function handleRepaymentClick(repayment_id: string) {
    closeAccountingTransactionModal()
    closeModalAndNavigate(setLocation, `/deduction/${repayment_id}`)
  }

  // All repayments when the detail endpoint provides them; fall back to the legacy single link.
  const detail = deduction as DeductionDetailJson
  const repayment_rows: { id: string; amount: number; date?: string }[] = (detail.repayments ?? []).map(
    (r: RepaymentSummary) => ({ id: r.id, amount: r.amount, date: r.check_date ?? r.invoice_date })
  )
  if (!repayment_rows.length && detail.repayment_id && detail.repayment_amount) {
    repayment_rows.push({ id: detail.repayment_id, amount: detail.repayment_amount })
  }

  // Sales invoice ↔ on-invoice discount links (detail endpoint only)
  const sales_invoice = detail.sales_invoice
  const sales_invoice_discounts = detail.sales_invoice_discounts ?? []

  return (
    <div>
      <div class="flex flex-col mt-1 mb-2">
        <EditableTransactionAmount
          deduction_id={deduction.id}
          kind={deduction.kind}
          invoice_amount={deduction.invoice_amount}
          currency={deduction.currency}
        />
        {deduction.kind === "invoice" && (deduction.discount_amount || deduction.gross_amount) ? (
          <div class="flex flex-col text-sm text-gray-500 mt-1">
            {deduction.gross_amount > 0 && <span>Gross: {exactCurrencyFormatter(deduction.gross_amount, deduction.currency)}</span>}
            {deduction.discount_amount > 0 && (
              <span>Discount: {exactCurrencyFormatter(deduction.discount_amount, deduction.currency)}</span>
            )}
          </div>
        ) : null}
        {deduction.kind !== "repayment" && detail.repayment_amount && repayment_rows.length === 1 ? (
          <div
            onClick={() => handleRepaymentClick(repayment_rows[0].id)}
            class="flex items-center gap-1 text-sm text-green-600 font-medium mt-1 cursor-pointer hover:text-green-700 hover:underline"
          >
            <span>Repaid: {exactCurrencyFormatter(detail.repayment_amount, deduction.currency)}</span>
            <ExternalLinkIcon className="h-4 w-4" />
          </div>
        ) : null}
        {deduction.kind !== "repayment" && detail.repayment_amount && repayment_rows.length > 1 ? (
          <div class="flex flex-col mt-1">
            <span class="text-sm text-green-600 font-medium">Repaid: {exactCurrencyFormatter(detail.repayment_amount, deduction.currency)}</span>
            {repayment_rows.map(row => (
              <div
                key={row.id}
                onClick={() => handleRepaymentClick(row.id)}
                class="flex items-center gap-1 text-sm text-green-600 cursor-pointer hover:text-green-700 hover:underline"
              >
                <span>
                  {exactCurrencyFormatter(row.amount, deduction.currency)}
                  {row.date ? ` on ${format(parseISO(row.date), "PP")}` : ""}
                </span>
                <ExternalLinkIcon className="h-3.5 w-3.5" />
              </div>
            ))}
          </div>
        ) : null}
        {sales_invoice ? (
          <div
            onClick={() => handleRepaymentClick(sales_invoice.id)}
            class="flex items-center gap-1 text-sm text-blue-600 font-medium mt-1 cursor-pointer hover:text-blue-700 hover:underline"
          >
            <span>
              Invoice {sales_invoice.invoice_number}: {exactCurrencyFormatter(Math.abs(sales_invoice.amount), deduction.currency)}
            </span>
            <ExternalLinkIcon className="h-4 w-4" />
          </div>
        ) : null}
        {sales_invoice_discounts.length > 0 ? (
          <div class="flex flex-col mt-1">
            <span class="text-sm text-blue-600 font-medium">Invoice discounts:</span>
            {sales_invoice_discounts.map(row => (
              <div
                key={row.id}
                onClick={() => handleRepaymentClick(row.id)}
                class="flex items-center gap-1 text-sm text-blue-600 cursor-pointer hover:text-blue-700 hover:underline"
              >
                <span>
                  {row.description || "Discount"}: {exactCurrencyFormatter(row.amount, deduction.currency)}
                </span>
                <ExternalLinkIcon className="h-3.5 w-3.5" />
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <h3 class="text-gray-500">{format(parseISO(deduction.invoice_date), "PP")}</h3>
      <div class="flex gap-8 items-center">
        <h1 class="text-3xl text-primary" onClick={copyOnAltClick(deduction.id)}>
          {deduction.invoice_number || deduction.description}
        </h1>
        {children}
      </div>
      <h3 class="text-gray-500">
        {(deduction as any).customer_name} {kind}
      </h3>
      {deduction.description && deduction.invoice_number && <p class="text-gray-500">{deduction.description}</p>}
    </div>
  )
}

export function DeductionHeaderRight({ deduction, children }: DeductionHeaderProps) {
  return (
    <div class="flex flex-col">
      {children}
    </div>
  )
}

interface AccountingTransactionProps {
  params: { id: Uuid }
}

export function AccountingTransaction({ params }: AccountingTransactionProps) {
  const statusFlag = RefreshFlag.use((sf: boolean) => sf!)

  let { data: transaction } = useFetch<TransactionJson>(
    () => api_fetch_throws<TransactionJson>(`/transaction/${params.id}`),
    [statusFlag, params.id]
  )

  if (!transaction) {
    return <CenterSpinner />
  }

  return (
    <div class="w-full">
      <DetailNavbar />
      <AccountingTransactionContent transaction={transaction} />
    </div>
  )
}

